import { createTheme } from '@mui/material/styles';
import { BREAKPOINT } from 'common/Constant';

// Single-pass theme using function styleOverrides to access the theme
const MUICustomTheme = createTheme({
  palette: {
    primary: { main: '#2F54A5' },
    secondary: { main: '#f50057' },
    tertiary: '#C0C0C0'
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'a, a:visited': {
          transition: 'none',
          outline: 'none'
        },
        'a:focus, a:focus-visible': {
          outline: 'none !important',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          transition: 'none',
          boxShadow: 'none'
        },
        'a:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          transition: 'none'
        },
        '*:focus': {
          outline: 'none !important',
          boxShadow: 'none !important'
        },
        '*:focus-visible': {
          outline: 'none !important',
          boxShadow: 'none !important'
        },
        'div[tabindex]:focus, div[tabindex]:focus-visible': {
          outline: 'none !important',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          boxShadow: 'none !important',
          border: 'none !important'
        },
        'span[tabindex]:focus, span[tabindex]:focus-visible': {
          outline: 'none !important',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          boxShadow: 'none !important',
          border: 'none !important'
        },
        '[role="button"]:focus, [role="button"]:focus-visible': {
          outline: 'none !important',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          boxShadow: 'none !important',
          border: 'none !important'
        }
      }
    },
    // Global: focused InputLabel/FormLabel uses black
    MuiInputLabel: {
      styleOverrides: {
        root: { '&.Mui-focused': { color: '#000000' } }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: { '&.Mui-focused': { color: '#000000' } }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            transition: 'none',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              transition: 'none'
            }
          }
        },
        option: ({ theme }) => ({
          transition: 'none',
          // Focused (hover/keyboard) option – use a light brand background but ensure readable text
          '&[data-focus="true"]': {
            backgroundColor: `${theme.palette.primary.light} !important`,
            color: `${theme.palette.getContrastText(theme.palette.primary.light)} !important`,
            transform: 'none'
          },
          // Some MUI builds add .Mui-focused instead of data-focus
          '&.Mui-focused': {
            backgroundColor: `${theme.palette.primary.light} !important`,
            color: `${theme.palette.getContrastText(theme.palette.primary.light)} !important`,
            transform: 'none'
          },
          // Selected option – strong contrast with brand primary
          '&[aria-selected="true"]': {
            backgroundColor: `${theme.palette.primary.light} !important`,
            color: `${theme.palette.getContrastText(theme.palette.primary.light)} !important`,
            transform: 'none'
          },
          // Keep selected highlight consistent on hover/focus
          '&[aria-selected="true"].Mui-focused, &[aria-selected="true"]:hover': {
            backgroundColor: `${theme.palette.primary.light} !important`,
            color: `${theme.palette.getContrastText(theme.palette.primary.light)} !important`,
            transform: 'none'
          }
        })
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.getContrastText(theme.palette.primary.light)
          },
          '&:hover': { color: 'black' }
        })
      }
    },
    MuiCheckbox: {
      defaultProps: { color: 'primary' },
      styleOverrides: {
        root: ({ theme }) => ({
          // Checked / indeterminate icon color follows brand
          '&.Mui-checked, &.MuiCheckbox-indeterminate': {
            color: theme.palette.primary.main
          },
          // Ensure nested SvgIcon also follows brand
          '&.Mui-checked .MuiSvgIcon-root': {
            color: theme.palette.primary.main
          }
        })
      }
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: ({ theme }) => ({
          '&.Mui-checked': {
            color: theme.palette.primary.main,
            '& + .MuiSwitch-track': {
              opacity: 0.7,
              backgroundColor: theme.palette.primary.main
            }
          }
        })
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }) => ({ backgroundColor: theme.palette.primary.main })
      }
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        root: {
          // Force transparent background for selected tabs/buttons, but exclude ToggleButtons (like SegmentedTabs and ListItemButton)
          '&.Mui-selected:not(.MuiToggleButton-root):not(.MuiListItemButton-root):not(.MuiMenuItem-root)': {
            backgroundColor: 'transparent !important'
          },
          '&.Mui-selected:hover:not(.MuiToggleButton-root):not(.MuiListItemButton-root):not(.MuiMenuItem-root)': {
            backgroundColor: 'transparent !important'
          },
          '&.Mui-selected:focus:not(.MuiToggleButton-root):not(.MuiListItemButton-root):not(.MuiMenuItem-root)': {
            backgroundColor: 'transparent !important'
          },
          '&.Mui-selected:focus-visible:not(.MuiToggleButton-root):not(.MuiListItemButton-root):not(.MuiMenuItem-root)': {
            backgroundColor: 'transparent !important'
          }
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          minWidth: 0,
          '@media (min-width: 0px)': { minWidth: 0 },
          textTransform: 'none',
          fontSize: 14,
          color: 'rgba(0, 0, 0, 0.54)',
          textDecoration: 'none',
          transition: 'none',
          outline: 'none',
          '&:hover:not(.Mui-selected)': { 
            textDecoration: 'none', 
            color: theme.palette.primary.dark,
            transition: 'none'
          },
          '&:focus:not(.Mui-selected)': { 
            textDecoration: 'none', 
            color: theme.palette.primary.dark,
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            transition: 'none',
            outline: 'none'
          },
          '&:focus-visible:not(.Mui-selected)': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            transition: 'none',
            outline: 'none'
          },
          '&:active': { textDecoration: 'none' },
          '& .MuiTab-wrapper': { textDecoration: 'none' },
          '& a': { textDecoration: 'none !important' },
          '&:hover a': { textDecoration: 'none' },
          '&:focus a': { textDecoration: 'none' },
          '&:active a': { textDecoration: 'none' },
          '&.Mui-selected': {
            color: theme.palette.primary.main,
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 500,
            backgroundColor: 'transparent !important',
            outline: 'none !important',
            boxShadow: 'none !important',
            border: 'none !important',
            '&::before': {
              display: 'none !important'
            },
            '&::after': {
              display: 'none !important'
            },
            '& *': {
              transition: 'none !important',
              animation: 'none !important'
            }
          }
        })
      }
    },
    MuiTextField: {
      defaultProps: { variant: 'standard' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            transition: 'none',
            '& .MuiOutlinedInput-notchedOutline': {
              transition: 'none'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              transition: 'none'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              transition: 'none'
            }
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          transition: 'none',
          '&:hover': {
            transition: 'none'
          },
          '&.Mui-focused': {
            transition: 'none'
          }
        },
        notchedOutline: {
          transition: 'none'
        }
      }
    },
    MuiSelect: {
      defaultProps: {
        variant: 'standard'
      },
      styleOverrides: {
        select: {
          '&:focus': {
            outline: 'none'
          }
        }
      }
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        root: {
          transition: 'none',
          transform: 'none',
          '&:focus': {
            outline: 'none',
            transform: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          },
          '&:focus-visible': {
            outline: 'none',
            transform: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          },
          '&:hover': {
            outline: 'none',
            transform: 'none',
            backgroundColor: '#D8E6FF'
          },
          '&:active': {
            transform: 'none'
          }
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        root: {
          transition: 'none',
          transform: 'none',
          '&:focus': {
            outline: 'none',
            transform: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          },
          '&:focus-visible': {
            outline: 'none',
            transform: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          },
          '&:hover': {
            transform: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          },
          '&:active': {
            transform: 'none'
          }
        }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          transition: 'none',
          outline: 'none',
          '&:focus': {
            outline: 'none !important',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            transition: 'none',
            boxShadow: 'none'
          },
          '&:focus-visible': {
            outline: 'none !important',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            transition: 'none',
            boxShadow: 'none'
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            transition: 'none'
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          transition: 'none',
          '&:focus': {
            outline: 'none !important',
            boxShadow: 'none'
          },
          '&:focus-visible': {
            outline: 'none !important',
            boxShadow: 'none'
          }
        }
      }
    },
    MuiCardActionArea: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        root: {
          transition: 'none',
          '&:focus': {
            outline: 'none !important',
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          },
          '&:focus-visible': {
            outline: 'none !important',
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }
      }
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          transition: 'none'
        }
      }
    },
    MuiToggleButton: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        root: {
          transition: 'none',
          transform: 'none',
          outline: 'none !important',
          '&:focus': {
            outline: 'none !important',
            boxShadow: 'none !important',
            transform: 'none'
          },
          '&:focus-visible': {
            outline: 'none !important',
            boxShadow: 'none !important',
            transform: 'none'
          },
          '&:hover': {
            transform: 'none'
          },
          '&:active': {
            transform: 'none'
          },
          // Only apply grey background to non-segmented toggle buttons (Performance Metrics filters)
          '&:not(.MuiToggleButtonGroup-grouped):focus': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          },
          '&:not(.MuiToggleButtonGroup-grouped):focus-visible': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          },
          // For Performance Metrics toggle buttons only
          '&.MuiToggleButtonGroup-grouped:not(.Mui-selected):focus': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          },
          '&.MuiToggleButtonGroup-grouped:not(.Mui-selected):focus-visible': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }
      }
    },
    MuiChip: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        root: {
          transition: 'none',
          transform: 'none',
          overflow: 'visible !important',
          '&:focus': {
            outline: 'none !important',
            boxShadow: 'none !important',
            transform: 'none',
            backgroundColor: 'transparent !important'
          },
          '&:focus-visible': {
            outline: 'none !important',
            boxShadow: 'none !important',
            transform: 'none',
            backgroundColor: 'transparent !important'
          },
          '&:hover': {
            transform: 'none',
            backgroundColor: 'transparent !important'
          },
          '&:active': {
            transform: 'none',
            backgroundColor: 'transparent !important'
          },
          '& .MuiChip-label': {
            overflow: 'visible',
            position: 'relative',
            zIndex: 1
          }
        },
        clickable: {
          '&:focus': {
            backgroundColor: 'transparent !important'
          },
          '&:focus-visible': {
            backgroundColor: 'transparent !important'
          },
          '&:hover': {
            backgroundColor: 'transparent !important'
          }
        }
      }
    }
  },
  // Override with custom Figma breakpoints
  breakpoints: {
    values: {
      xs: BREAKPOINT.mobile,         // 0–767px
      sm: BREAKPOINT.tablet,         // 768–1279px
      md: BREAKPOINT.smallDesktop,   // 1280–1439px
      lg: BREAKPOINT.desktop,        // 1440–1559px
      xl: BREAKPOINT.largeDesktop,   // 1560px+
    },
  }
});

export default MUICustomTheme;
